// GenericVsGrounded: the same owner question answered twice. A general-purpose chatbot (no names)
// gives a fair but generic answer; the unit's own assistant answers for that serial, with its source.
// Honest framing: the generic answer is not wrong, it just is not about this unit.
import React from 'react';
import { cx } from '../ui';
import type { HeroUnit } from '../heroUnits';
import { defaultUnit, qaFor, type Tone } from './data';
import { IconDoc, IconPerson, useAiStyles } from './shared';

export type GenericVsGroundedProps = { tone?: Tone; unit?: HeroUnit; className?: string };

const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

function Marked({ text, marks }: { text: string; marks: string[] }) {
  const re = new RegExp(`(${marks.map(esc).join('|')})`, 'g');
  return (
    <>
      {text.split(re).map((p, i) => (marks.includes(p) ? <mark key={i}>{p}</mark> : <React.Fragment key={i}>{p}</React.Fragment>))}
    </>
  );
}

const X = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
    <path d="M4.5 4.5l7 7M11.5 4.5l-7 7" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);
const Tick = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
    <path d="M3 8.4 6.4 11.6 13 4.6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export function GenericVsGrounded({ tone = 'light', unit = defaultUnit, className }: GenericVsGroundedProps) {
  useAiStyles();
  const qa = qaFor(unit);
  const facts: [string, string][] = [
    ['Model and build', unit.model],
    ['This serial', unit.serial],
    ['Warranty', qa.cover],
    [unit.partnerRole[0].toUpperCase() + unit.partnerRole.slice(1), unit.partner],
    ['Source', qa.cite],
  ];
  const marks = [unit.model, unit.partner, 'under warranty', 'Yours was last descaled 260 hours ago', 'This unit is at 4,060', 'fits your unit'].filter((m) => qa.a.includes(m));

  return (
    <div className={cx('ai-vs', className)} data-tone={tone}>
      <div className="ai-vs-q">
        <span className="ai-vs-q-l">The owner asks</span>
        <span className="ai-vs-q-t">“{qa.q}”</span>
      </div>
      <div className="ai-vs-grid">
        <section className="ai-vs-card ai-vs-gen" aria-label="A general-purpose chatbot">
          <header className="ai-vs-h">
            <span className="ai-vs-who">A general-purpose chatbot</span>
            <span className="ai-vs-tag">Guesses</span>
          </header>
          <p className="ai-vs-a">{qa.generic}</p>
          <ul className="ai-vs-facts">
            {facts.map(([k]) => (
              <li key={k}>
                <span className="ai-vs-ico is-no">
                  <X />
                </span>
                <span className="ai-vs-fk">{k}</span>
                <span className="ai-vs-fv">Unknown</span>
              </li>
            ))}
          </ul>
          <footer className="ai-vs-f">A fair answer for any {unit.noun}. The owner still has to ring you.</footer>
        </section>

        <section className="ai-vs-card ai-vs-own" aria-label={`The ${unit.brand} assistant for this unit`}>
          <header className="ai-vs-h">
            <span className="ai-vs-who">
              <span className="ai-vs-mark" style={{ background: unit.accent }}>
                {unit.brand[0]}
              </span>
              {unit.brand} assistant, for this unit
            </span>
            <span className="ai-vs-tag is-ok">Knows</span>
          </header>
          <p className="ai-vs-a">
            <Marked text={qa.a} marks={marks} />
          </p>
          <ul className="ai-vs-facts">
            {facts.map(([k, v]) => (
              <li key={k}>
                <span className="ai-vs-ico is-yes">
                  <Tick />
                </span>
                <span className="ai-vs-fk">{k}</span>
                <span className={cx('ai-vs-fv', k === 'This serial' && 'font-mono')}>
                  {k === 'Source' && <IconDoc size={12} />} {v}
                </span>
              </li>
            ))}
          </ul>
          <footer className="ai-vs-f ai-vs-f-own">
            <span className="ai-vs-btn" style={{ background: unit.accent }}>
              {qa.action}
            </span>
            <span className="ai-vs-human">
              <IconPerson size={13} /> Talk to a person
            </span>
          </footer>
        </section>
      </div>
    </div>
  );
}

export default GenericVsGrounded;
